import { useState, useEffect, useCallback, useRef } from 'react'
import { authenticatedFetch } from '../services/api/apiClient'
import logger from '../utils/logger'

/**
 * Custom hook for data fetching with loading, error, and refetch support
 *
 * @param {string} url - The API endpoint URL
 * @param {object} options - Configuration options
 * @param {object} options.fetchOptions - Options to pass to fetch (method, body, headers)
 * @param {any} options.initialData - Initial data value (default: null)
 * @param {boolean} options.skip - Skip the fetch if true (useful for conditional fetching)
 * @param {Array} options.dependencies - Additional dependencies to trigger refetch
 * @param {function} options.transform - Transform function for response data
 * @param {string} options.errorMessage - Custom error message prefix
 * @param {boolean} options.fetchOnMount - Whether to fetch on mount (default: true)
 *
 * @returns {object} { data, loading, error, refetch, setData }
 *
 * @example
 * // Simple GET request
 * const { data, loading, error } = useFetch('/api/users')
 *
 * @example
 * // With dependencies (refetches when campaignId changes)
 * const { data, loading } = useFetch(`/api/campaigns/${campaignId}`, {
 *   dependencies: [campaignId],
 *   skip: !campaignId
 * })
 *
 * @example
 * // POST request with body
 * const { data, loading, refetch } = useFetch('/api/users', {
 *   fetchOptions: {
 *     method: 'POST',
 *     body: JSON.stringify({ name: 'John' })
 *   },
 *   fetchOnMount: false
 * })
 * // Then call refetch() when needed
 */
const useFetch = (url, options = {}) => {
  const {
    fetchOptions = {},
    initialData = null,
    skip = false,
    dependencies = [],
    transform = null,
    errorMessage = 'Failed to fetch data',
    fetchOnMount = true
  } = options

  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(!skip && fetchOnMount)
  const [error, setError] = useState(null)

  const mountedRef = useRef(true)
  const abortControllerRef = useRef(null)
  const transformRef = useRef(transform)
  transformRef.current = transform
  const errorMessageRef = useRef(errorMessage)
  errorMessageRef.current = errorMessage

  const fetchData = useCallback(async () => {
    if (skip) {
      setLoading(false)
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    try {
      setLoading(true)
      setError(null)

      const response = await authenticatedFetch(url, {
        ...fetchOptions,
        signal: abortControllerRef.current.signal
      })

      if (!mountedRef.current) return

      if (!response.ok) {
        let errorMsg = `${errorMessageRef.current}: ${response.status}`

        try {
          const errorBody = await response.json()
          if (errorBody && errorBody.message) {
            errorMsg = errorBody.message
          }
        } catch {
        }

        throw new Error(errorMsg)
      }

      const result = await response.json()

      if (!mountedRef.current) return

      const transformedData = transformRef.current ? transformRef.current(result) : result
      setData(transformedData)
    } catch (err) {
      if (err.name === 'AbortError') return

      if (!mountedRef.current) return

      logger.error(errorMessageRef.current, { error: err.message, url })
      setError(err.message)
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [url, skip, JSON.stringify(fetchOptions)])

  useEffect(() => {
    if (fetchOnMount) {
      fetchData()
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchData, ...dependencies])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    setData
  }
}

/**
 * Hook for making mutations (POST, PUT, DELETE) with loading state
 *
 * @param {string} url - The API endpoint URL
 * @param {object} options - Configuration options
 * @param {string} options.method - HTTP method (default: 'POST')
 * @param {function} options.onSuccess - Callback on successful mutation
 * @param {function} options.onError - Callback on error
 * @param {string} options.errorMessage - Custom error message prefix
 *
 * @returns {object} { mutate, loading, error, data }
 *
 * @example
 * const { mutate, loading } = useMutation('/api/users', {
 *   method: 'POST',
 *   onSuccess: (data) => console.log('Created:', data)
 * })
 *
 * // Call with body
 * mutate({ name: 'John', email: 'john@example.com' })
 */
export const useMutation = (url, options = {}) => {
  const {
    method = 'POST',
    onSuccess = null,
    onError = null,
    errorMessage = 'Mutation failed'
  } = options

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const mutate = useCallback(async (body = null, mutateOptions = {}) => {
    const { urlSuffix = '' } = mutateOptions

    try {
      setLoading(true)
      setError(null)

      const fetchOpts = {
        method,
        ...(body && { body: JSON.stringify(body) })
      }

      const response = await authenticatedFetch(`${url}${urlSuffix}`, fetchOpts)

      if (!mountedRef.current) return

      if (!response.ok) {
        let errorMsg = `${errorMessage}: ${response.status}`

        try {
          const errorBody = await response.json()
          if (errorBody && errorBody.message) {
            errorMsg = errorBody.message
          }
        } catch {
        }

        throw new Error(errorMsg)
      }

      let result = null
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        result = await response.json()
      }

      if (!mountedRef.current) return

      setData(result)

      if (onSuccess) {
        onSuccess(result)
      }

      return result
    } catch (err) {
      if (!mountedRef.current) return

      logger.error(errorMessage, { error: err.message, url })
      setError(err.message)

      if (onError) {
        onError(err)
      }

      throw err
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [url, method, errorMessage, onSuccess, onError])

  return {
    mutate,
    loading,
    error,
    data,
    reset: useCallback(() => {
      setData(null)
      setError(null)
    }, [])
  }
}

export default useFetch
